/**
 * Component: Service plan form
 * ------------------------------------------------------------------------------
 * Form functionality for service plans page.
 *
 * @namespace spForm
 */
import log from 'salvo-lite/log'
import axios from 'axios'
import {formatMoney} from '@shopify/theme-currency'
import debounce from 'lodash-es/debounce'

import breakpoints from '../helpers/breakpoints'
import cssClasses from '../helpers/cssClasses'
import {on} from '../helpers/utils'

class ServicePlansForm {
    constructor (theme, elem) {
        this._elem = elem
        this._settings = {
            container: '[js-sp-form="container"]',
            // All steps
            monthlyCost: '[js-sp-form="monthlyCost"]',
            monthlyPayment: '[js-monthly-payment="element"]',
            totalCost: '[js-sp-form="totalCost"]',
            validity: '[js-sp-form="validity"]',
            // Step 1
            step1: '[js-sp-form="step1"]',
            annualMileage: '[js-sp-form="annualMileage"]',
            annualMileageError: '[js-sp-form="annualMileageError"]',
            slider: '[js-sp-form="slider"]',
            sliderValue: '[js-sp-form="sliderValue"]',
            duration: '[js-sp-form="duration"]',
            confirmation: '[js-sp-form="confirmation"]',
            status: '[js-sp-form="status"]',
            statusCopy: '[js-sp-form="statusCopy"]',
            costs: '[js-sp-form="costs"]',
            // Step 2
            step2: '[js-sp-form="step2"]',
            step2Save: '[js-sp-form="step2Save"]',
            field: '[js-sp-form="field"]',
            // Step 3
            step3: '[js-sp-form="step3"]',
            planType: '[js-sp-form="planType"]',
            years: '[js-sp-form="years"]',
            distance: '[js-sp-form="distance"]',
            purchaseDate: '[js-sp-form="purchaseDate"]',
            addToCart: '[js-sp-form="addToCart"]',
            ...JSON.parse(elem.getAttribute('data-service-plans-form') || '{}')
        }

        /**
         * DOM node selectors.
         */
        const step1 = document.querySelector(this._settings.step1)
        const step2 = document.querySelector(this._settings.step2)
        const step3 = document.querySelector(this._settings.step3)

        this._nodeSelectors = {
            container: document.querySelector(this._settings.container),
            // All steps
            monthlyCost: [...document.querySelectorAll(this._settings.monthlyCost)],
            monthlyPayment: [...document.querySelectorAll(this._settings.monthlyPayment)],
            totalCost: [...document.querySelectorAll(this._settings.totalCost)],
            // Step 1
            annualMileage: step1.querySelector(this._settings.annualMileage),
            annualMileageError: step1.querySelector(this._settings.annualMileageError),
            slider: step1.querySelector(this._settings.slider),
            sliderValue: step1.querySelector(this._settings.sliderValue),
            duration: [...step1.querySelectorAll(this._settings.duration)],
            confirmation: step1.querySelector(this._settings.confirmation),
            status: step1.querySelector(this._settings.status),
            statusCopy: step1.querySelector(this._settings.statusCopy),
            costs: step1.querySelector(this._settings.costs),
            // Step 2
            step2Save: step2.querySelector(this._settings.step2Save),
            field: [...step2.querySelectorAll(this._settings.field)],
            // Step 3
            planType: step3.querySelector(this._settings.planType),
            years: step3.querySelector(this._settings.years),
            distance: step3.querySelector(this._settings.distance),
            purchaseDate: step3.querySelector(this._settings.purchaseDate),
            addToCart: step3.querySelector(this._settings.addToCart)
        }

        /**
         * Global data object.
         */
        this._planData = {}
        this._annualMileage = false
        this._duration = false
        this._sliderEnabled = false
        this._locale = 'en-GB'
        this._annualMileageErrorState = false

        if (window.theme.store.id === 'de_store') {
            this._locale = 'de-DE'
        }

        /**
         * Event used to automatically update annual mileage value.
         */
        this._event = document.createEvent('HTMLEvents')
        this._event.initEvent('keyup', false, true)

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setComponentStates()
        this._setDefaultState()
        this._setListeners()
        this._setEventListeners()
        this._sliderCheck()
    }

    /**
   * Check component functionality.
   */
    _setComponentStates () {
        if (this._nodeSelectors.annualMileage) {
            this._annualMileage = true
        }

        if (this._nodeSelectors.duration.length > 0) {
            this._duration = true
        }
    }

    /**
     * Set default form state.
     */
    _setDefaultState () {
        this._planData = {}

        if (this._annualMileage) {
            if (this._sliderEnabled) {
                this._nodeSelectors.slider.value = Number(this._nodeSelectors.slider.getAttribute('data-default'))
                this._handleSliderUpdate()
                this._handleSliderMileageUpdate()
            } else {
                this._updateData('annualMileage', 0)
                this._nodeSelectors.annualMileage.value = ''
                this._updateFieldValidity(this._nodeSelectors.annualMileage, false)
            }
        }

        if (this._duration) {
            this._nodeSelectors.duration.forEach((element, index) => {
                if (index === 0) {
                    element.checked = true
                    this._updateData('duration', Number(element.value))
                    return
                }

                element.checked = false
            })
        }

        if (!this._annualMileage) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
        }

        /**
         * Set information from currentCar.
         */
        if (window.Frame.CurrentCarCookie.get()) {
            const currentCar = window.Frame.CurrentCarCookie.get()

            this._updateData('emissionCode', currentCar.emissionCode)
            this._updateData('transmissionCode', currentCar.transmissionCode)
            this._updateData('engineCode', currentCar.engineCode)
            this._updateData('productionDate', currentCar.productionDate)
            this._updateData('vehicleCode', currentCar.vehicleCode)
            this._updateData('vinNumber', currentCar.vinNumber)
        }
    }

    /**
     * Set trigger listeners.
     */
    _setListeners () {
        on('resize', debounce(this._sliderCheck, 250))

        if (this._annualMileage) {
            on('keyup', this._nodeSelectors.annualMileage, () => this._handleAnnualMileageChange())
        }

        if (this._duration) {
            this._nodeSelectors.duration.forEach((element) => {
                on('change', element, () => this._handleDurationChange(element))
            })
        }

        on('click', this._nodeSelectors.confirmation, () => this._handleConfirmationChange())

        this._nodeSelectors.field.forEach((element) => {
            on('keyup', element, () => this._handleFieldChange(element))
        })

        on('click', this._nodeSelectors.step2Save, () => this._handleStep2Save())
        on('click', this._nodeSelectors.addToCart, () => this._handleAddToCart())
    }

    /**
     * Set eventBus listeners.
     */
    _setEventListeners () {
        window.Frame.EventBus.listen([
            'CurrentCar:updated',
            'CurrentCar:clear',
            'CurrentCar:cleared'
        ], () => this._resetForm())

        window.Frame.EventBus.listen('AjaxCart:itemAdded', () => this._handleItemAdded())
    }

    /**
     * Handle resize event and check whether to init slider.
     */
    _sliderCheck () {
        if (!this._annualMileage) {
            return
        }

        if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
            this._sliderInit()
            return
        }

        this._sliderEnabled = false
    }

    /**
     * Initialise slider.
     */
    _sliderInit () {
        this._sliderEnabled = true
        /**
         * Set value of mileage input.
         */
        this._nodeSelectors.annualMileage.value = this._nodeSelectors.slider.value
        this._nodeSelectors.annualMileage.dispatchEvent(this._event)

        /**
         * Set triggers.
         */
        on('input', this._nodeSelectors.slider, () => this._handleSliderUpdate())
        on('change', this._nodeSelectors.slider, () => this._handleSliderUpdate())
        on('mouseup', this._nodeSelectors.slider, () => this._handleSliderMileageUpdate())
    }

    /**
     * Handle changes.
     * ------------------------------------------------------------------------------
     * Actions form change when form is interacted with.
     *
     */

    /**
     * Handle annual mileage change.
     */
    _handleAnnualMileageChange () {
        this._nodeSelectors.annualMileageError.innerText = ''

        const value = Number(this._nodeSelectors.annualMileage.value)
        this._updateData('annualMileage', value)

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }

        if (this._isAnnualMileageValid()) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
            this._nodeSelectors.slider.value = value
            this._nodeSelectors.sliderValue.innerText = value.toLocaleString(this._locale)
            this._updateFieldValidity(this._nodeSelectors.annualMileage, true)
            return
        }

        this._nodeSelectors.confirmation.setAttribute('disabled', true)
        this._updateFieldValidity(this._nodeSelectors.annualMileage, false)

        /**
         * Show min/max errors.
         */
        if (value === 0) {
            return
        }

        /**
         * If they haven't yet entered enough characters, don't show error.
         */
        if (this._nodeSelectors.annualMileage.value.length < 4 && !this._annualMileageErrorState) {
            return
        }

        this._annualMileageErrorState = true
        const min = Number(this._nodeSelectors.annualMileage.getAttribute('min'))
        const max = Number(this._nodeSelectors.annualMileage.getAttribute('max'))
        let errorString = ''

        if (value < min) {
            errorString = `${min.toLocaleString(this._locale)} ${window.theme.strings.standard.miles} ${window.theme.strings.standard.minimum}`
        } else if (value > max) {
            errorString = `${max.toLocaleString(this._locale)} ${window.theme.strings.standard.miles} ${window.theme.strings.standard.maximum}`
        }

        this._nodeSelectors.annualMileageError.innerText = errorString
    }

    /**
     * Handle contract duration change.
     * @param {HTMLElement} element - The selected element.
     */
    _handleDurationChange (element) {
        this._updateData('duration', Number(element.value))

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }

        if (this._isAnnualMileageValid()) {
            this._nodeSelectors.confirmation.removeAttribute('disabled')
        } else {
            this._nodeSelectors.confirmation.setAttribute('disabled', true)
        }
    }

    /**
     * Handle confirmation checkbox change.
     */
    _handleConfirmationChange () {
        if (this._nodeSelectors.confirmation.checked) {
            if (this._isAnnualMileageValid()) {
                this._submitSearch()
            } else {
                this._handleError('Invalid mileage')
            }

            return
        }

        if (this._isFormSubmitted()) {
            this._resetFormSubmission()
        }
    }

    /**
     * Handles step 2 field changes.
     * @param {HTMLElement} element - The changed element.
     */
    _handleFieldChange (element) {
        if (element.value === '') {
            this._updateFieldValidity(element, false)
        } else {
            this._updateFieldValidity(element, true)
        }

        if (this._isStep2Valid()) {
            this._nodeSelectors.step2Save.removeAttribute('disabled')
        } else {
            this._nodeSelectors.step2Save.setAttribute('disabled', true)
        }
    }

    /**
     * Handle the step 2 address being saved/updated.
     */
    _handleStep2Save () {
        if (!this._isStep2Valid()) {
            return
        }

        if (this._nodeSelectors.step2Save.getAttribute('data-state') === 'update') {
            this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.updating
            this._nodeSelectors.step2Save.setAttribute('disabled', true)

            window.setTimeout(() => {
                this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.update
                this._nodeSelectors.step2Save.removeAttribute('disabled')
            }, 2000)
        } else {
            this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.update
        }

        this._nodeSelectors.field.forEach((element) => {
            const key = element.getAttribute('data-input')
            let value = element.value

            if (key === 'currentMileage') {
                value = Number(element.value)
            }

            this._updateData(key, value)
        })

        this._nodeSelectors.container.setAttribute('data-form-step', 'step3')
        this._nodeSelectors.step2Save.setAttribute('data-state', 'update')
        this._nodeSelectors.addToCart.removeAttribute('disabled')
    }

    /**
     * Handle add to cart.
     */
    _handleAddToCart () {
        this._nodeSelectors.addToCart.innerText = window.theme.strings.addingToCart
        this._nodeSelectors.addToCart.setAttribute('disabled', true)

        let name = `${this._planData.firstName} ${this._planData.lastName}`
        let address = `${this._planData.address1}, ${this._planData.city}, ${this._planData.zip}, ${this._planData.country}`

        if (this._planData.middleInitial !== '') {
            name = `${this._planData.firstName} ${this._planData.middleInitial} ${this._planData.lastName}`
        }

        if (this._planData.address2 !== '') {
            address = `${this._planData.address1}, ${this._planData.address2}, ${this._planData.city}, ${this._planData.zip}, ${this._planData.country}`
        }

        const id = Number(this._nodeSelectors.addToCart.getAttribute('data-id'))
        const options = {
            quantity: 1,
            properties: {
                name,
                address,
                currentMileage: this._planData.current,
                distance: this._planData.distance,
                vehicleCode: this._planData.vehicleCode,
                engineCode: this._planData.engineCode,
                emissionCode: this._planData.emissionCode,
                vinNumber: this._planData.vinNumber
            }
        }

        if (this._duration) {
            options.properties.duration = this._planData.duration
        }

        window.Frame.EventBus.emit('AjaxCart:addItem', {id, options})
    }

    /**
     * Handle item added successfully.
     */
    _handleItemAdded () {
        this._nodeSelectors.addToCart.innerText = window.theme.strings.addedToCart

        window.setTimeout(() => {
            this._resetForm()

            window.scroll({
                behavior: 'smooth',
                left: 0,
                top: 0
            })
        }, 3000)
    }

    /**
     * Update slider value instantly.
     */
    _handleSliderUpdate () {
        const value = Number(this._nodeSelectors.slider.value)
        const displayValue = value.toLocaleString(this._locale)

        this._nodeSelectors.sliderValue.innerText = displayValue
    }

    /**
     * Update annual mileage on release to reduce overhead.
     */
    _handleSliderMileageUpdate () {
        this._nodeSelectors.annualMileage.value = this._nodeSelectors.slider.value
        this._nodeSelectors.annualMileage.dispatchEvent(this._event)
    }

    /**
     * Global functions.
     * ------------------------------------------------------------------------------
     * Global functions to action form events.
     *
     */

    /**
     * Update global data.
     * @param {String} key - The key to update.
     * @param {String} value - The value to update the key with.
     */
    _updateData (key, value) {
        this._planData[key] = value

        switch (key) {
        case 'annualMileage':
            this._planData.distance = `${value.toLocaleString(this._locale)} ${window.theme.strings.standard.miles}`
            break

        case 'currentMileage':
            this._planData.current = `${value.toLocaleString(this._locale)} ${window.theme.strings.standard.miles}`
            break

        case 'duration':
            this._planData.years = `${value} ${window.theme.strings.standard.years}`
            break
        }
    }

    /**
     * Render step 3.
     * @param {Object} product - The service plan product.
     */
    _renderStep3 (product) {
        this._nodeSelectors.planType.innerHTML = product.description

        if (this._annualMileage) {
            this._nodeSelectors.distance.innerText = this._planData.distance
        }

        if (this._duration) {
            this._nodeSelectors.years.innerText = this._planData.years
        }
    }

    /**
     * Update the add to cart button with product variant ID.
     * @param {Object} product - The service plan product.
     */
    _updateAddToCart (product) {
        this._nodeSelectors.addToCart.setAttribute('data-id', product.variants[0].id)
    }

    /**
     * Resets the entire form.
     */
    _resetForm () {
        this._setDefaultState()
        this._resetFormSubmission()
    }

    /**
     * Resets everything after annual mileage and contract duration.
     */
    _resetFormSubmission () {
        this._nodeSelectors.container.setAttribute('data-form-step', 'step0')
        this._nodeSelectors.container.setAttribute('data-form-submitted', 'false')

        /**
         * Step 1.
         */
        this._nodeSelectors.confirmation.checked = false

        if (this._annualMileage || this._duration) {
            if (!this._isAnnualMileageValid() && !this._sliderEnabled) {
                this._nodeSelectors.confirmation.setAttribute('disabled', true)
            }
        }

        this._nodeSelectors.status.classList.remove(cssClasses.hidden)
        this._nodeSelectors.status.classList.remove(cssClasses.loading)
        this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.step1.waiting
        this._nodeSelectors.costs.classList.remove(cssClasses.active)
        this._nodeSelectors.monthlyCost.innerText = formatMoney(0, window.theme.moneyFormat)
        this._nodeSelectors.totalCost.innerText = formatMoney(0, window.theme.moneyFormat)

        /**
         * Step 2.
         */
        this._nodeSelectors.field.forEach((element) => {
            if (element.getAttribute('data-input') === 'country') {
                return
            }

            element.value = ''
            this._updateFieldValidity(element, false)
        })

        this._nodeSelectors.step2Save.setAttribute('disabled', true)
        this._nodeSelectors.step2Save.innerText = window.theme.strings.servicePlans.step2.continue

        /**
         * Step 3.
         */
        this._nodeSelectors.planType.innerText = ''
        this._nodeSelectors.addToCart.innerText = window.theme.strings.addToCart

        if (this._annualMileage) {
            this._nodeSelectors.distance.innerText = ''
        }

        if (this._duration) {
            this._nodeSelectors.years.innerText = ''
        }
    }

    /**
     * Form functions.
     * ------------------------------------------------------------------------------
     * Functions responsible for retrieving the data.
     *
     */

    /**
     * Submit search for service plans.
     */
    _submitSearch () {
        this._nodeSelectors.container.setAttribute('data-form-submitted', true)
        this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.step1.loading
        this._nodeSelectors.status.classList.add(cssClasses.loading)

        let searchValue = `product_type:ESB+tag:Engine_Code-${this._planData.engineCode}+tag:Vehicle_Code-${this._planData.vehicleCode}+tag:Emission_Code-${this._planData.emissionCode}`
        const transmissionValue = `product_type:ESB+tag:Transmission_Code-${this._planData.transmissionCode}`

        if (this._duration) {
            searchValue += `+tag:Duration-${this._planData.duration}`
        }

        this._retrieveServicePlans(transmissionValue)
            .then((response) => {
                if (response.length > 0) {
                    searchValue += `+tag:Transmission_Code-${this._planData.transmissionCode}`
                    this._searchForServicePlan(searchValue)
                } else {
                    this._searchForServicePlan(searchValue)
                }

                return response
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    _searchForServicePlan (searchValue) {
        this._retrieveServicePlans(searchValue)
            .then((response) => {
                if (response.length > 0) {
                    if (window.theme.dev) {
                        window.console.log('Service plan search response:', response)
                    }
                    window.Frame.EventBus.emit('ServicePlan:hasPlan', true)
                    this._filterProducts(response)
                    return
                }
                window.Frame.EventBus.emit('ServicePlan:hasPlan', false)
                throw new Error('No products')
            })
            .catch((error) => {
                this._handleError(error.message || error)
            })
    }

    /**
     * Retrieve service plan products.
     * @param {String} searchValue - The search string.
     */
    _retrieveServicePlans (searchValue) {
        return new Promise((resolve, reject) => {
            axios.get(`/search?type=product&q=${searchValue}&view=sku`)
                .then((response) => {
                    if (response) {
                        resolve(response.data.products)
                        return
                    }

                    throw new Error('No response')
                })
                .catch((error) => reject(error))
        })
    }

    /**
     * Filter products by mileage and production date based on form values.
     * Can return no products so receiving _must handle this.
     * @param {Array} products - The products in the response.
     */
    _filterProducts (products) {
        const date = this._calculateDate(this._planData.productionDate)
        let filteredProducts = products.filter((product) => this._filterByProductionDate(product, date) !== false)

        if (this._annualMileage) {
            filteredProducts = filteredProducts.filter((product) => this._filterByMileage(product, this._planData.annualMileage) !== false)
        }

        if (window.theme.dev) {
            window.console.log('Service plan filtered products', filteredProducts)
        }

        if (filteredProducts.length > 1) {
            return this._handleError('Too many products', products)
        }

        if (filteredProducts.length === 0) {
            return this._handleError('No products')
        }

        return this._renderResponse(filteredProducts)
    }

    /**
     * Render the filtered product's details.
     * @param {Object} product - The filtered product.
     */
    _renderResponse (product) {
        const singleProduct = product[0]

        /**
         * Render monthly cost.
         * - If no monthly cost tag then hide all monthly payment content.
         */
        let monthlyCost = this._retrieveTag(singleProduct.tags, 'Monthly_Cost')

        if (monthlyCost) {
            this._nodeSelectors.monthlyPayment.forEach((element) => {
                element.classList.remove(cssClasses.hidden)
            })

            monthlyCost = monthlyCost.replace('Monthly_Cost:', '').trim()
            monthlyCost = formatMoney(monthlyCost, window.theme.moneyFormat)
            this._nodeSelectors.monthlyCost.forEach((element) => {
                element.innerText = monthlyCost
            })
        } else {
            this._nodeSelectors.monthlyPayment.forEach((element) => {
                element.classList.add(cssClasses.hidden)
            })
        }

        /**
         * Render total cost.
         */
        const totalCost = formatMoney(singleProduct.price, window.theme.moneyFormat)
        this._nodeSelectors.totalCost.forEach((element) => {
            element.innerText = totalCost
        })

        this._nodeSelectors.status.classList.add(cssClasses.hidden)
        this._nodeSelectors.costs.classList.add(cssClasses.active)

        /**
         * Continue form.
         */
        this._nodeSelectors.container.setAttribute('data-form-step', 'step2')
        this._renderStep3(singleProduct)
        this._updateAddToCart(singleProduct)
    }

    /**
     * Utility functions.
     * ------------------------------------------------------------------------------
     * Helper functions to check statuses etc.
     *
     */

    /**
     * Checks if the annual mileage value is valid.
     * @returns {Boolean}
     */
    _isAnnualMileageValid () {
        if (!this._annualMileage) {
            return true
        }

        const regex = RegExp('^[0-9]*$')
        const annualMileageValue = Number(this._nodeSelectors.annualMileage.value)

        if (!regex.test(annualMileageValue)) {
            return false
        }

        const min = this._nodeSelectors.annualMileage.getAttribute('min')
        const max = this._nodeSelectors.annualMileage.getAttribute('max')

        return (annualMileageValue >= min) && (annualMileageValue <= max)
    }

    /**
     * Checks if the form has been submimtted yet.
     * @returns {Boolean}
     */
    _isFormSubmitted () {
        const formSubmitted = this._nodeSelectors.container.getAttribute('data-form-submitted')

        if (formSubmitted === 'true') {
            return true
        }

        return false
    }

    /**
     * Checks if step 2 fields are all valid.
     * @returns {Boolean}
     */
    _isStep2Valid () {
        const invalidFields = this._nodeSelectors.field.filter((element) => {
            return (element.hasAttribute('required')) && (element.value === '')
        })

        return invalidFields.length === 0
    }

    /**
     * Handle form errors.
     * @param {String} errorType - The type of error.
     * @param {Array} products - Products which caused the error.
     */
    _handleError (errorType, products) {
        switch (errorType) {
        case 'No products':
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.noProducts
            break

        case 'Too many products':
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.lookup
            break

        case 'Invalid mileage':
            this._nodeSelectors.statusCopy.innerText = window.theme.strings.servicePlans.errors.incomplete
            break
        }

        this._nodeSelectors.status.classList.remove(cssClasses.loading)

        if (products) {
            throw new Error(errorType, products)
        }

        throw new Error(errorType)
    }

    /**
     * Updates a fields validity status.
     * @param {HTMLElement} field - The field.
     * @param {Boolean} valid - Whether it's valid or not.
     */
    _updateFieldValidity (field, valid) {
        const parent = field.closest(this._settings.validity)

        if (valid) {
            parent.classList.add(cssClasses.valid)
            return
        }

        parent.classList.remove(cssClasses.valid)
    }

    /**
     * Filter products by the production date tag on each product.
     * @param {Object} product - The product to test.
     * @param {String} date - The production date.
     * @returns {Object}
     */
    _filterByProductionDate (product, date) {
        const matchValue = 'Build_Date'
        let tag = this._retrieveTag(product.tags, matchValue)

        // If it doesn't require a certain date, return
        if (tag === '') {
            return true
        }

        // Retrieve info from the tag
        tag = tag.split('--')
        tag = tag[1].split('_')

        // Set type (before/after) and required date
        const type = tag[0]
        let requiredDate = tag[1]
        requiredDate = this._calculateDate(requiredDate)

        if ((type === 'Before') && (date.getTime() < requiredDate.getTime())) {
            return true
        } else if ((type === 'After') && (date.getTime() >= requiredDate.getTime())) {
            return true
        }

        return false
    }

    /**
     * Filter products by the mileage tag on each product.
     * @param {Object} product - The product to test.
     * @param {String} mileage - The mileage entered in the form.
     */
    _filterByMileage (product, mileage) {
        const matchValue = 'Annual_Mileage'
        let tags = this._retrieveTag(product.tags, matchValue)

        if (tags.length > 1) {
            return this._checkMileageMeetsTagConditions(mileage, tags)
        } else {
            let tag = tags[0]

            // If it doesn't require a certain date, return
            if (tag === '') {
                return true
            }

            // Retrieve info from the tag
            tag = tag.split('--')
            tag = tag[1].split('_')
            // Set type (LT/GTE) and required mileage
            const type = tag[0]
            const requiredMileage = Number(tag[1])
            if ((type === 'LT') && (mileage < requiredMileage)) {
                return true
            } else if ((type === 'GTE') && (mileage >= requiredMileage)) {
                return true
            }
            return false
        }
    }

    /**
     * Return true if mileage meets Annual_Mileage tag conditions
     * @param {Number} mileage
     * @param {Array} tags
     */
    _checkMileageMeetsTagConditions (mileage, tags) {
        if (tags.length <= 1) return false

        let LT = 0
        let GTE = 0

        tags.forEach((tag) => {
            if (tag.includes('LT')) {
                tag = tag.split('_')
                LT = parseInt(tag[2])
            }

            if (tag.includes('GTE')) {
                tag = tag.split('_')
                GTE = parseInt(tag[2])
            }
        })

        if ((mileage >= GTE) && (mileage < LT)) {
            return true
        }

        return false
    }

    /**
     * Create a UNIX compatible date value.
     * @param {String} dateValue - The date string.
     * @returns {String}
     */
    _calculateDate (dateValue) {
        const date = dateValue.split('-')
        const year = Number(date[0])
        const month = (Number(date[1]) - 1)
        const day = (date[2]) ? Number(date[2]) : 1

        return new Date(year, month, day)
    }

    /**
     * Retrieve the required tag.
     * @param {Object} tags - The tags object.
     * @param {String} matchValue - The value to match.
     * @returns {String}
     */
    _retrieveTag (tags, matchValue) {
        let match = ''

        if (matchValue === 'Annual_Mileage') {
            match = []
        }

        tags.forEach((tag) => {
            if (tag.includes(matchValue)) {
                if (matchValue === 'Annual_Mileage') {
                    match.push(tag)
                } else {
                    match = tag
                }
            }
        })

        return match
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // });
}

export default ServicePlansForm



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ServicePlansForm.js